import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import { countSeens } from "../containers/student/video/func";
import { loadVimeoPlayerApi, parseVimeoSource } from "../helpers/vimeoPlayer";
import { videoDataRequest } from "../modules/students-modules/videos/Actions";

const VimeoEmbed = ({ videoUrl, data, trackProgress = true }) => {
  const iframeRef = useRef(null);
  const playerRef = useRef(null);
  const isPlayingRef = useRef(false);
  const lastTimeRef = useRef(0);
  const playedDeltaRef = useRef(0);
  const hasCountedSeenRef = useRef(false);
  const dataRef = useRef(data);
  dataRef.current = data;

  const dispatch = useDispatch();
  const userData = useSelector((state) => state.user.userData);
  const videoData = useSelector((state) => state.chapterVideos.videoData);
  const [searchParams] = useSearchParams();
  const { id: routeId, video_id: routeVideoId } = useParams();

  const parsed = parseVimeoSource(videoUrl);
  const embedUrl = parsed?.embedUrl;

  useEffect(() => {
    if (!embedUrl || !iframeRef.current || !trackProgress) return;

    let cancelled = false;
    let player;
    isPlayingRef.current = false;
    lastTimeRef.current = 0;
    playedDeltaRef.current = 0;
    hasCountedSeenRef.current = false;

    const getVideoId = () =>
      dataRef.current?.id ||
      searchParams.get("video") ||
      routeVideoId ||
      routeId;

    const reportProgress = (currentTime, playedDelta) => {
      const videoId = getVideoId();
      if (!videoId || !userData?.id) return;
      if (!currentTime && !playedDelta) return;

      dispatch(
        videoDataRequest({
          total_played: playedDelta || 0,
          total_covered: currentTime || 0,
          video_id: videoId,
          user_id: userData.id,
        })
      );
    };

    const flushProgress = () => {
      const currentTime = lastTimeRef.current;
      const playedDelta = playedDeltaRef.current;
      playedDeltaRef.current = 0;
      reportProgress(currentTime, playedDelta);
    };

    loadVimeoPlayerApi()
      .then((Player) => {
        if (cancelled || !iframeRef.current) return;

        player = new Player(iframeRef.current);
        playerRef.current = player;

        player.on("play", () => {
          isPlayingRef.current = true;
          if (!hasCountedSeenRef.current && dataRef.current) {
            hasCountedSeenRef.current = true;
            countSeens(dispatch, dataRef.current);
          }
        });

        player.on("pause", () => {
          isPlayingRef.current = false;
          flushProgress();
        });
        player.on("ended", () => {
          isPlayingRef.current = false;
          flushProgress();
        });

        player.on("timeupdate", (event) => {
          const seconds = event?.seconds || 0;
          if (isPlayingRef.current) {
            const delta = seconds - lastTimeRef.current;
            if (delta > 0 && delta < 2) {
              playedDeltaRef.current += delta;
            }
          }
          lastTimeRef.current = seconds;
        });
      })
      .catch(() => {});

    const onHidden = () => {
      if (document.visibilityState === "hidden") {
        isPlayingRef.current = false;
        flushProgress();
      }
    };

    document.addEventListener("visibilitychange", onHidden);
    window.addEventListener("beforeunload", flushProgress);

    return () => {
      cancelled = true;
      isPlayingRef.current = false;
      document.removeEventListener("visibilitychange", onHidden);
      window.removeEventListener("beforeunload", flushProgress);
      if (player) {
        player.off("play");
        player.off("pause");
        player.off("ended");
        player.off("timeupdate");
        if (typeof player.destroy === "function") player.destroy();
      }
      playerRef.current = null;
    };
  }, [
    embedUrl,
    trackProgress,
    dispatch,
    userData?.id,
    searchParams,
    routeId,
    routeVideoId,
  ]);

  useEffect(() => {
    if (videoData?.views_count > 3) {
      window.location.reload();
    }
  }, [videoData]);

  if (!parsed) {
    return <div dangerouslySetInnerHTML={{ __html: videoUrl }}></div>;
  }

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        paddingBottom: "56.25%",
      }}
    >
      <iframe
        ref={iframeRef}
        src={parsed.embedUrl}
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
        title="Vimeo video"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          border: 0,
        }}
      />
    </div>
  );
};

export default VimeoEmbed;
